import {
  CartSnapshot,
  DecisionSnapshot,
  SnapshotsGroupedByTrackId,
  UserDecisionPublic,
} from "@/shared/types";
import { findClosest, findHighestCloseItem } from "../utils";
import { ObservableObject, observable, opaqueObject } from "@legendapp/state";

const CACHE_RESOLUTION = 1000;

import CartWorker from "@/workers/cart-buffer?worker";

export type CartSnapshotsBuffer = Record<
  string,
  {
    cart: UserDecisionPublic[];
    cartTotal: number;
    snapshotTimes: Set<number>;
    snapshots: Map<number, CartSnapshot[]>;
  }
>;

const timestampObject = new Date();

const worker = new CartWorker();

export class CartBufferManager {
  private buffer: ObservableObject<CartSnapshotsBuffer>; // Buffer to store the received objects
  private startTime: Date | null = null;
  private startTimeAnchor: Date | null = null;
  private largestTimestamp: Date | null = null;

  setStartTime(startTime: Date) {
    this.startTime = startTime;
    this.startTimeAnchor = new Date(startTime);
    this.startTimeAnchor.setMilliseconds(0);
  }

  constructor(_treshold?: number) {
    this.buffer = observable<CartSnapshotsBuffer>({});
  }

  public getTimestampIndex(timestamp: Date) {
    if (!this.startTimeAnchor) return -1;

    timestamp.setMilliseconds(0);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return Math.round((timestamp - this.startTimeAnchor) / CACHE_RESOLUTION);
  }

  public addObjects(
    tracks: SnapshotsGroupedByTrackId<DecisionSnapshot>[]
  ): void {
    /**
     * Add the received objects to the buffer via worker.
     */

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    worker.onmessage = function handleProcessedBuffer(event) {
      self.buffer.set(event.data.buffer);
      self.largestTimestamp = event.data.largestTimestamp;
    };

    worker.postMessage({
      tracks,
      buffer: opaqueObject(this.buffer.peek()),
      largestTimestamp: this.largestTimestamp,
    });

    if (!this.startTime) {
      this.startTime = new Date();
    }
  }

  public getKeys(): string[] {
    /**
     * Retrieve the track ids from the buffer.
     * Adjust this logic based on how you retrieve the track ids from the objects.
     */
    return Object.keys(this.buffer);
  }

  /**
   * Check if the buffer reached the limit.
   */
  public isLimitReached(): boolean {
    const buffer = this.buffer.peek();

    const keys = Object.keys(buffer);

    if (keys.length === 0) return false;

    // if ( > 0) {
    // const lastObject = this.buffer[this.buffer.length - 1];
    // const firstObject = this.buffer[0];
    // const timeDifference = lastObject.datetime - firstObject.datetime;

    // return timeDifference >= this.treshold;
    // }

    return false;
  }

  public getSnapshotToDraw(key: string, datetime: number): CartSnapshot | null {
    /**
     * Retrieve the objects from the buffer to draw on the canvas.
     * Adjust this logic based on how you retrieve and draw the points on the canvas.
     */
    timestampObject.setTime(datetime);
    const timestampIndex = this.getTimestampIndex(timestampObject);
    const trackBuffer = this.buffer.peek()?.[key];

    if (timestampIndex < 0 || !trackBuffer) return null;

    const itemSnapshots = trackBuffer.snapshots.get(timestampIndex);

    if (!itemSnapshots) {
      const prevSnapshot = this.getLatestSnapshot(
        trackBuffer.snapshots,
        timestampIndex,
        datetime
      );

      return prevSnapshot;
    }

    const snapshotIndex = findClosest(datetime, itemSnapshots);

    return trackBuffer.snapshots.get(timestampIndex)?.[snapshotIndex] ?? null;
  }

  private getLatestSnapshot(
    cache: Map<number, CartSnapshot[]>,
    timestampIndex: number,
    timestamp: number
  ): CartSnapshot | null {
    const keys = Array.from(cache.keys());
    const closestSnapshotGroup = findHighestCloseItem(keys, timestampIndex);
    const itemSnapshots = cache.get(closestSnapshotGroup);

    if (itemSnapshots === undefined) return null;

    const snapshotIndex = findClosest(timestamp, itemSnapshots);
    return itemSnapshots[snapshotIndex];
  }

  public watchTracks() {
    /**
     * Watch for changes in the buffer.
     */
    return Object.keys(this.buffer);
  }

  public get currentSize() {
    /**
     * Return the current size of the buffer.
     */
    return Object.keys(this.buffer).length;
  }

  public clear() {
    /**
     * Clear the buffer.
     */
    this.buffer = observable<CartSnapshotsBuffer>();
  }
}
